import { t } from '@if/core/i18n'
import { getNumberFromQueryString, getStringFromQueryString } from '@if/core/utils'
import { Box, Button, Paper } from '@mui/material'
import { DataGrid, GridFooter, GridFooterContainer, GridPaginationModel } from '@mui/x-data-grid'
import { useGetLoanDashboardEntries } from '@src/api/loan/loan-dashboard-api'
import { useSelectedCompany } from '@src/providers/SelectedCompanyProvider'
import { falsy } from '@src/types/constants'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LoanDashBoardFiltersSchemaType } from './loan-filters-schema'
import { useLoanListColumns } from './LoanList-hooks'
import LoanListOptions from './LoanListOptions'

const LoanList = () => {
  const selectedCompany = useSelectedCompany()
  const [searchParams, setSearchParams] = useSearchParams()

  const filterValues = useMemo<LoanDashBoardFiltersSchemaType>(() => {
    return {
      limit: getNumberFromQueryString(searchParams, 'limit', 50),
      offset: getNumberFromQueryString(searchParams, 'offset', 0),
      financingCompanyId: selectedCompany,
      searchTerm: getStringFromQueryString(searchParams, 'searchTerm'),
    }
  }, [searchParams, selectedCompany])

  const buildSearchParams = useCallback(
    (values: LoanDashBoardFiltersSchemaType): URLSearchParams => {
      const updateSearch = new URLSearchParams(searchParams)
      updateSearch.set('limit', encodeURIComponent(values.limit))
      updateSearch.set('offset', encodeURIComponent(values.offset))
      updateSearch.set('financingCompanyId', encodeURIComponent(values.financingCompanyId))

      if (values.searchTerm) {
        updateSearch.set('searchTerm', values.searchTerm)
      } else {
        updateSearch.delete('searchTerm')
      }
      return updateSearch
    },
    [searchParams],
  )

  const handleSearch = useCallback(
    (searchTerm: string) => {
      setSearchParams(buildSearchParams({ ...filterValues, searchTerm, offset: 0 }), { replace: true })
    },
    [buildSearchParams, filterValues, setSearchParams],
  )

  const columns = useLoanListColumns()
  const dashboardEntriesSearchParam = useMemo(() => buildSearchParams(filterValues), [filterValues, buildSearchParams])
  const [data, isPending] = useGetLoanDashboardEntries(dashboardEntriesSearchParam)

  const paginationModel = useMemo(
    () => ({ pageSize: filterValues.limit, page: filterValues.offset / filterValues.limit }),
    [filterValues.limit, filterValues.offset],
  )

  const handlePageSizeChange = (model: GridPaginationModel) => {
    if (model.pageSize !== filterValues.limit) {
      setSearchParams(buildSearchParams({ ...filterValues, limit: model.pageSize, offset: 0 }))
    }
  }

  const handleOnPageChange = (state: number) => {
    const updateSearch = new URLSearchParams(searchParams)
    const newOffset = state === 0 ? filterValues.offset - filterValues.limit : filterValues.offset + filterValues.limit
    updateSearch.set('offset', encodeURIComponent(newOffset))
    setSearchParams(updateSearch, { replace: true })
  }

  const customFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
        <div style={{ display: 'flex' }}>
          <Button onClick={() => handleOnPageChange(0)} disabled={filterValues.offset === 0}>
            {' '}
            {t('common.previous')}
          </Button>
          <Button onClick={() => handleOnPageChange(1)} disabled={data.length < filterValues.limit}>
            {' '}
            {t('common.next')}
          </Button>
        </div>
      </GridFooterContainer>
    )
  }

  return (
    <>
      <LoanListOptions searchTerm={filterValues.searchTerm ?? ''} onSearch={handleSearch} />
      <Paper>
        <Box textAlign="center">
          <div style={{ height: '45rem' }}>
            <DataGrid
              disableColumnFilter
              isRowSelectable={falsy}
              getRowId={(row) => row.loanId}
              rows={data}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePageSizeChange}
              loading={isPending}
              paginationMode="server"
              rowCount={data.length}
              slots={{
                footer: customFooter,
              }}
              slotProps={{
                pagination: {
                  labelDisplayedRows: () =>
                    t('common.customDisplayedRowsTextFooter.displayRange', {
                      defaultValue: '',
                      from: filterValues.offset + 1,
                      to: filterValues.offset + filterValues.limit,
                    }),
                },
              }}
            />
          </div>
        </Box>
      </Paper>
    </>
  )
}

export default LoanList
